// PaymentTracker.jsx — Brief 50 (13 Jul 2026) Payment Request Tracker.
//
// Mirrors the shape of InvoiceTracker.jsx per V's spec: same header
// layout, same summary cards, same table density, same row-click into
// the existing detail page. The lifecycle model is simpler than
// InvoiceTracker's — no dual-dataset (pipeline + credit-control) merge;
// payment requests carry their own lifecycle in ``payment_status``
// (Pending -> Approved -> Sent -> Complete with a Cancelled sink).
//
// Route: /finance/payment-tracker (registered in App.jsx). Visible to
// director/MD and ACCOUNTS role only (route-gate enforced client-side;
// authoritative gate lives in the backend list endpoint at
// GET /hub/finance/payment-requests).

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CreditCard, Loader2, Search, RefreshCw,
  Clock, ChevronRight, Plus, PoundSterling,
} from "lucide-react";
import { API } from "../config/api";
import { isInterAccount } from "../lib/paymentTypes";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { getUserRole, ROLES } from "../config/roleConfig";
import { useSmartSearchResolver } from "../hooks/useSmartSearchResolver";

// ─── auth headers (same shape as InvoiceTracker) ─────────────────────────────
function authHeaders() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email =
    user.user_email || user.email || user.ms_email ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function fmtGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  if (typeof raw === "string" && /[£$€]/.test(raw)) return raw.trim();
  const n = Number(typeof raw === "string" ? raw.replace(/[,\s]/g, "") : raw);
  if (!Number.isFinite(n)) return typeof raw === "string" ? raw : "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

// ─── Status + Payment Type badges ────────────────────────────────────────────

const STATUS_BADGES = {
  Pending:   { bg: "bg-slate-500/15", text: "text-slate-300", label: "Pending" },
  Approved:  { bg: "bg-blue-500/15", text: "text-blue-300", label: "Approved" },
  Sent:      { bg: "bg-status-amber/15", text: "text-status-amber", label: "Sent" },
  Complete:  { bg: "bg-teal/15", text: "text-teal", label: "Complete" },
  Cancelled: { bg: "bg-status-red/15", text: "text-status-red", label: "Cancelled" },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGES[status] || STATUS_BADGES.Pending;
  return (
    <span
      data-testid={`row-status-${status || "Pending"}`}
      className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

function PaymentTypeBadge({ paymentType }) {
  const interAccount = isInterAccount(paymentType);
  return (
    <span
      data-testid={`row-payment-type-${paymentType || "Third-Party"}`}
      className={
        "px-2 py-0.5 rounded text-xs font-medium " +
        (interAccount
          ? "bg-teal/15 text-teal border border-teal/30"
          : "bg-purple-500/15 text-purple-300 border border-purple-500/30")
      }
    >
      {paymentType || "Third-Party"}
    </span>
  );
}

// Item 24 (Brief 50 follow-up, 15 Jul 2026) — compact PDF-status badge
// for the tracker row. Reuses the exact StatusBadge styling above
// (same padding/rounded-full/whitespace-nowrap shape); only the colour
// map + labels differ, mirroring _PAYMENT_STATUS_STYLES in
// PaymentRequestDetail.jsx so the two surfaces read identically.
const PDF_STATUS_BADGES = {
  not_generated: { bg: "bg-slate-500/15", text: "text-slate-300", label: "Not Generated" },
  generated:     { bg: "bg-status-amber/15", text: "text-status-amber", label: "Generated" },
  filed:         { bg: "bg-teal/15", text: "text-teal", label: "Filed" },
  failed:        { bg: "bg-status-red/15", text: "text-status-red", label: "Failed" },
};

function PdfStatusBadge({ status }) {
  // Defensive read — rows that predate this field render as
  // "Not Generated" rather than blank/undefined.
  const s = PDF_STATUS_BADGES[status] || PDF_STATUS_BADGES.not_generated;
  return (
    <span
      data-testid={`row-pdf-status-${status || "not_generated"}`}
      className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

// Next-valid transition (server-authoritative — this is UI hint only).
function _next_transition(current, isDirector, isCashier) {
  const s = current || "Pending";
  // BM-H3516 (Vanessa, 3 Oct 2026): Accounts may approve too; the server
  // refuses anyone approving their own request.
  if (s === "Pending" && (isDirector || isCashier)) return { to: "Approved", label: "Approve" };
  if (s === "Approved" && (isDirector || isCashier)) return { to: "Sent", label: "Mark Sent" };
  if (s === "Sent" && (isDirector || isCashier)) return { to: "Complete", label: "Mark Complete" };
  return null;
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({ search, setSearch, statusFilter, setStatusFilter, paymentTypeFilter, setPaymentTypeFilter, feeEarnerFilter, setFeeEarnerFilter, dateFrom, setDateFrom, dateTo, setDateTo, onRefresh, refreshing }) {
  return (
    <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="payment-tracker-filters">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search matter, client, payee, PMT#..." data-testid="payment-tracker-search"
            className="w-full bg-navy border border-white/10 rounded pl-9 pr-3 py-2 text-sm text-white" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="payment-tracker-filter-status"
          className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white">
          <option value="">All statuses</option>
          {Object.keys(STATUS_BADGES).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)}
          data-testid="payment-tracker-filter-payment-type"
          className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white">
          <option value="">All types</option>
          {["CTO", "OTC", "OTS", "Third-Party"].map((pt) => <option key={pt} value={pt}>{pt}</option>)}
        </select>
        <input type="text" value={feeEarnerFilter} onChange={(e) => setFeeEarnerFilter(e.target.value)}
          placeholder="Fee earner initials"
          data-testid="payment-tracker-filter-fee-earner"
          className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" />
        <div className="flex gap-2">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="payment-tracker-filter-date-from"
            className="bg-navy border border-white/10 rounded px-2 py-2 text-xs text-white w-full" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="payment-tracker-filter-date-to"
            className="bg-navy border border-white/10 rounded px-2 py-2 text-xs text-white w-full" />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing}
          data-testid="payment-tracker-refresh"
          className="text-xs text-gray-400 hover:text-white">
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}

// ─── Summary cards ──────────────────────────────────────────────────────────

function SummaryCards({ rows, activeFilter, onFilter }) {
  const counts = rows.reduce((acc, r) => {
    const st = r.payment_status || "Pending";
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const cards = [
    { key: "Pending",   label: "Pending",   value: counts.Pending   || 0, valueClass: (counts.Pending || 0) > 0 ? "text-slate-300" : "text-white" },
    { key: "Approved",  label: "Approved",  value: counts.Approved  || 0, valueClass: (counts.Approved || 0) > 0 ? "text-blue-300" : "text-white" },
    { key: "Sent",      label: "Sent",      value: counts.Sent      || 0, valueClass: (counts.Sent || 0) > 0 ? "text-status-amber" : "text-white" },
    { key: "Complete",  label: "Complete",  value: counts.Complete  || 0, valueClass: (counts.Complete || 0) > 0 ? "text-teal" : "text-white" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="payment-tracker-summary-cards">
      {cards.map((c) => (
        <button key={c.key} onClick={() => onFilter(activeFilter === c.key ? "" : c.key)}
          data-testid={`payment-tracker-summary-${c.key}`}
          className={`bg-navy-card border rounded-lg p-4 text-left transition-colors ${
            activeFilter === c.key ? "border-teal/60" : "border-white/10 hover:border-white/20"
          }`}>
          <div className="text-xs text-gray-400 mb-1">{c.label}</div>
          <div className={`text-2xl font-bold ${c.valueClass}`}>{c.value}</div>
        </button>
      ))}
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────────────────────────

function TrackerRow({ row, isDirector, isCashier, navigate, onTransition, busyId }) {
  const nextTransition = _next_transition(row.payment_status, isDirector, isCashier);
  const canCancel = isDirector && !["Cancelled", "Complete"].includes(row.payment_status);
  const rowBusy = busyId === row.id;

  return (
    <tr
      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
      onClick={() => navigate(`/submission/${row.id}`)}
      data-testid={`payment-tracker-row-${row.id}`}
    >
      <td className="py-2.5 px-3 text-xs font-mono text-teal">{row.payment_number || "\u2014"}</td>
      <td className="py-2.5 px-3 text-sm text-white">{row.matter_reference || "\u2014"}</td>
      <td className="py-2.5 px-3 text-sm text-gray-300">{row.client_name || "\u2014"}</td>
      <td className="py-2.5 px-3"><PaymentTypeBadge paymentType={row.payment_type} /></td>
      <td className="py-2.5 px-3 text-sm text-white">{fmtGbp(row.amount) || "\u2014"}</td>
      <td className="py-2.5 px-3 text-xs text-gray-400">{row.requester_name || row.requester_email || "\u2014"}</td>
      <td className="py-2.5 px-3 text-xs text-gray-300">{row.fee_earner || "\u2014"}</td>
      <td className="py-2.5 px-3"><StatusBadge status={row.payment_status} /></td>
      <td className="py-2.5 px-3"><PdfStatusBadge status={row.pdf_status} /></td>
      <td className="py-2.5 px-3 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1">
          {nextTransition && (
            <Button size="sm" disabled={rowBusy}
              onClick={(e) => { e.stopPropagation(); onTransition(row, nextTransition.to, false); }}
              data-testid={`payment-tracker-action-${nextTransition.to}-${row.id}`}
              className="text-xs bg-teal/10 text-teal hover:bg-teal/20 px-2 py-1">
              {nextTransition.label}
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="ghost" disabled={rowBusy}
              onClick={(e) => { e.stopPropagation(); onTransition(row, "Cancelled", true); }}
              data-testid={`payment-tracker-action-Cancelled-${row.id}`}
              className="text-xs text-status-red hover:bg-status-red/10 px-2 py-1">
              Cancel
            </Button>
          )}
          <ChevronRight className="w-4 h-4 text-gray-500 self-center" />
        </div>
      </td>
    </tr>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function PaymentTracker() {
  const navigate = useNavigate();

  const userEmail = (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") || ""
  ).toLowerCase();
  const userRole = getUserRole(userEmail);
  const isDirector = userRole === ROLES.DIRECTOR;
  const isCashier = userRole === ROLES.ACCOUNTS;
  const canRead = isDirector || isCashier;
  // BM-H3509: everyone else (fee earners) gets their OWN requests, read
  // only. Mate's "payment requests awaiting you" nudge links fee earners
  // here, and the page used to refuse them outright.
  const mineMode = !canRead;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  // BM-H3488: honour ?fee_earner= from dashboard and Mate links.
  const [searchParams] = useSearchParams();
  const [feeEarnerFilter, setFeeEarnerFilter] = useState(
    () => (searchParams.get("fee_earner") || "").toUpperCase()
  );
  // BM-H3509: honour ?outstanding=1 (Mate nudge, dashboard links).
  const [outstandingOnly, setOutstandingOnly] = useState(
    () => searchParams.get("outstanding") === "1"
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cardFilter, setCardFilter] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (paymentTypeFilter) params.payment_type = paymentTypeFilter;
      if (feeEarnerFilter && !mineMode) params.fee_earner = feeEarnerFilter;
      if (mineMode) params.mine = true;
      if (outstandingOnly) params.outstanding_only = true;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      params.limit = 50;
      const res = await axios.get(`${API}/hub/finance/payment-requests`, {
        headers: authHeaders(),
        params,
      });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      const status = err?.response?.status;
      const reason = status === 401 || status === 403
        ? "You are not authorised to view the payment request tracker."
        : errorMessage(err, "Failed to load payment requests.");
      setError(reason);
      setRows([]);
      toast.error(reason);
    } finally {
      setLoading(false);
    }
  }, [mineMode, outstandingOnly, statusFilter, paymentTypeFilter, feeEarnerFilter, dateFrom, dateTo]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  async function onTransition(row, toStatus, requiresReason) {
    if (busyId) return;
    let reason = "";
    if (requiresReason) {
      reason = window.prompt(
        toStatus === "Cancelled" ? "Reason for cancelling? (min 3 chars)" : "Reason? (min 3 chars)",
        "",
      );
      if (reason === null) return;
      if ((reason || "").trim().length < 3) {
        toast.error("Reason must be at least 3 characters.");
        return;
      }
    }
    setBusyId(row.id);
    try {
      await axios.patch(
        `${API}/submissions/${row.id}/payment-status`,
        { new_status: toStatus, reason: (reason || "").trim() },
        { headers: authHeaders() },
      );
      toast.success(`Payment marked ${toStatus}`);
      await fetchRows();
    } catch (err) {
      toast.error(errorMessage(err, "Transition failed"));
    } finally {
      setBusyId(null);
    }
  }

  // Item 21-patch (#189): free-text search is resolved server-side via
  // /api/smart-search (search_resolver.resolve) instead of a client-side
  // substring match — "one resolver, everywhere" per
  // backend/hub/SEARCH_RESOLVER.md. /api/smart-search has no payment-tracker
  // scope param, so the card filter (by payment_status) stays client-side
  // on top of the resolved matter set.
  const { matchedRefs, matchedClientNames } = useSmartSearchResolver(search);

  // Card-filter overlay (server does the heavy lifting for status/type/fee
  // earner/date filters via fetchRows' params; free-text is resolved above).
  const visibleRows = useMemo(() => {
    const searching = search.trim().length >= 2;
    return rows.filter((r) => {
      if (cardFilter && (r.payment_status || "Pending") !== cardFilter) return false;
      if (!searching) return true;
      const ref = (r.matter_reference || "").toUpperCase();
      const clientLower = (r.client_name || "").toLowerCase();
      return matchedRefs.has(ref) || matchedClientNames.has(clientLower);
    });
  }, [rows, search, cardFilter, matchedRefs, matchedClientNames]);

  return (
    <div className="min-h-screen bg-navy p-6 text-white" data-testid="payment-tracker-page">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")}
          data-testid="payment-tracker-back"
          className="mb-2 -ml-2 text-xs text-slate-400 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Home
        </Button>
        {/* 27 Oct 2026 system-wide refresh: header band. */}
        <PageHero icon={CreditCard} title={mineMode ? "Your Payment Requests" : "Payment Request Tracker"}
          subtitle="Every payment request, from request to completion"
          tile="linear-gradient(135deg, #06b6d4, #3b82f6)" testId="payment-tracker-hero">
          <Button onClick={() => navigate("/payment-request")} size="sm"
            data-testid="payment-tracker-new">
            <Plus className="w-4 h-4 mr-1" /> New Payment Request
          </Button>
        </PageHero>

        {mineMode && (
          <p className="text-sm text-gray-400 mb-3" data-testid="payment-tracker-mine-note">
            These are the payment requests on your matters. Accounts and the directors approve and pay them.
          </p>
        )}

        <SummaryCards rows={rows} activeFilter={cardFilter} onFilter={setCardFilter} />

        <FilterBar
          search={search} setSearch={setSearch}
          statusFilter={statusFilter} setStatusFilter={(v) => { setOutstandingOnly(false); setStatusFilter(v); }}
          paymentTypeFilter={paymentTypeFilter} setPaymentTypeFilter={setPaymentTypeFilter}
          feeEarnerFilter={feeEarnerFilter} setFeeEarnerFilter={setFeeEarnerFilter}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          onRefresh={fetchRows} refreshing={loading}
        />

        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="payment-tracker-loading">
            <Loader2 className="w-6 h-6 animate-spin text-teal" />
          </div>
        ) : error ? (
          <div className="bg-status-red-dim/20 border border-status-red/40 rounded-md p-4 text-sm text-status-red"
            data-testid="payment-tracker-error">
            {error}
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="bg-navy-card border border-white/10 rounded-lg p-12 text-center"
            data-testid="payment-tracker-empty">
            <Clock className="w-8 h-8 mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-400">No payment requests match the current filters.</p>
          </div>
        ) : (
          <div className="bg-navy-card border border-white/10 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing <span className="text-white font-medium">{visibleRows.length}</span> of {total} records
              </span>
              {(outstandingOnly || statusFilter || paymentTypeFilter || feeEarnerFilter || dateFrom || dateTo || cardFilter) && (
                <button
                  onClick={() => {
                    setOutstandingOnly(false);
                    setStatusFilter(""); setPaymentTypeFilter(""); setFeeEarnerFilter("");
                    setDateFrom(""); setDateTo(""); setCardFilter("");
                  }}
                  data-testid="payment-tracker-clear-filters"
                  className="text-xs text-teal hover:text-teal-hover"
                >Clear filters</button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payment-tracker-table">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-white/10">
                    <th className="py-2 px-3">Payment #</th>
                    <th className="py-2 px-3">Matter Ref</th>
                    <th className="py-2 px-3">Client</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Amount</th>
                    <th className="py-2 px-3">Requested By</th>
                    <th className="py-2 px-3">Allocated To</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">PDF</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <TrackerRow
                      key={row.id}
                      row={row}
                      isDirector={isDirector}
                      isCashier={isCashier}
                      navigate={navigate}
                      onTransition={onTransition}
                      busyId={busyId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
